/** How an agent-type capability names the agent it runs.
 *
 * Either a tag (`latest` by default), resolved each time the tool is called so
 * a newly tagged version is picked up without editing the tool, or one pinned
 * version. The pinned form still carries `agent_id` so the form can show which
 * agent it belongs to; the runtime uses `agent_version_id` when present. */
export type AgentReference =
  | { mode: "tag"; agentId: number | null; tag: string }
  | { mode: "version"; agentId: number | null; versionId: number | null };

export const DEFAULT_AGENT_REFERENCE: AgentReference = {
  mode: "tag",
  agentId: null,
  tag: "latest",
};

const asId = (v: unknown): number | null =>
  typeof v === "number" && Number.isInteger(v) && v > 0 ? v : null;

/** Read a stored `execution_config` back into the form's shape. */
export function parseAgentReference(config: Record<string, unknown>): AgentReference {
  const agentId = asId(config.agent_id);
  const versionId = asId(config.agent_version_id);
  if (versionId !== null) return { mode: "version", agentId, versionId };
  return {
    mode: "tag",
    agentId,
    tag: typeof config.tag === "string" && config.tag ? config.tag : "latest",
  };
}

/** The `execution_config` to save, or an error message for the form. */
export function agentReferenceConfig(
  ref: AgentReference,
): { config: Record<string, unknown> } | { error: string } {
  if (ref.agentId === null) return { error: "Pick the agent this tool calls." };
  if (ref.mode === "version") {
    if (ref.versionId === null) return { error: "Pick the version this tool calls." };
    return { config: { agent_id: ref.agentId, agent_version_id: ref.versionId } };
  }
  const tag = ref.tag.trim();
  if (!tag) return { error: "Enter the tag to resolve, e.g. latest." };
  return { config: { agent_id: ref.agentId, tag } };
}
